import { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MousePointerClick, UserPlus, TrendingUp, Link2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Seo from "../components/Seo";

const TYPES = [
  { v: "blog", l: "Blog" },
  { v: "site", l: "Site web" },
  { v: "influenceur", l: "Influenceur / créateur" },
  { v: "autre", l: "Autre" },
];

// Page publique « Devenir partenaire » : explique l'objectif du partenariat et
// permet de créer son compte partenaire (inscription ouverte, ou via lien d'invitation).
export default function PartnerLanding() {
  const { code } = useParams();               // /partenaire/inscription/:code
  const [sp] = useSearchParams();             // /partenaire?invite=CODE
  const invite = code || sp.get("invite") || "";
  const nav = useNavigate();
  const { user, partnerRegister } = useAuth();
  const [f, setF] = useState({ name: "", ptype: "blog", email: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Si déjà connecté en tant que partenaire, renvoyer vers son espace.
  useEffect(() => {
    if (user && user.role === "partner") nav("/partenaire/espace");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("Les mots de passe ne correspondent pas.");
    if (f.password.length < 6) return toast.error("Mot de passe trop court (min 6 caractères).");
    setLoading(true);
    try {
      const payload = { name: f.name, email: f.email, password: f.password, ptype: f.ptype };
      if (invite) payload.invite_code = invite;
      await partnerRegister(payload);
      toast.success("Compte partenaire créé — bienvenue ! Voici votre espace.");
      nav("/partenaire/espace");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="grain grid min-h-screen bg-void lg:grid-cols-2">
      <Seo title="Espace Partenaire" description="Espace Partenaire APP CHALLENGE ACADEMY : diffusez votre lien unique et suivez en direct les inscriptions que vous générez (clics, inscrits, idées)." path="/partenaire" />
      <div className="absolute right-4 top-4 z-20"><LanguageSwitcher /></div>

      {/* Explication du partenariat */}
      <div className="relative hidden overflow-hidden border-r border-white/10 bg-surface lg:block">
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-cyan/15 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Programme partenaire</div>
            <h2 className="mt-4 font-display text-4xl font-900 leading-tight text-white">
              Partagez l'aventure, suivez votre communauté.
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-ink-secondary">
              <span className="text-white">Notre mission :</span> chacun dépose gratuitement son idée d'application ;
              chaque semaine, notre comité en sélectionne une et nous la développons <span className="text-cyan">gratuitement</span> pour son porteur.
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-secondary">
              <span className="text-white">Notre partenariat :</span> faire connaître cette opportunité et nous apporter des utilisateurs,
              via un <span className="text-cyan">lien unique</span> qui vous est attribué.
            </p>
            <ul className="mt-8 space-y-4">
              <Step icon={Link2} title="Votre lien unique" text="Diffusez-le sur votre blog, site, réseaux ou auprès de votre audience." />
              <Step icon={MousePointerClick} title="Chaque clic est compté" text="Le visiteur arrive sur la page d'inscription avec votre code." />
              <Step icon={UserPlus} title="Chaque inscription vous est rattachée" text="Vous savez exactement qui s'est inscrit grâce à vous." />
              <Step icon={TrendingUp} title="Un tableau de bord en direct" text="Clics, inscrits, connexions, idées déposées et taux de conversion." />
            </ul>
          </div>
          <div className="text-xs text-ink-tertiary">appchallengeacademy.com</div>
        </div>
      </div>

      {/* Formulaire de création de compte partenaire */}
      <div className="flex items-center justify-center overflow-y-auto px-5 py-16 sm:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white lg:hidden">
            <ArrowLeft size={15} /> Accueil
          </Link>
          <h1 className="font-display text-3xl font-700 text-white">Devenir partenaire</h1>
          <p className="mt-2 text-sm text-ink-secondary">
            Créez votre compte partenaire : vous recevrez par e-mail votre lien à diffuser et vos identifiants,
            et vous suivrez votre communauté dans votre espace dédié.
          </p>
          {invite && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-cyan/30 bg-cyan/10 px-3 py-2 text-xs text-cyan" data-testid="partner-invite-flag">
              <CheckCircle2 size={14} /> Invitation détectée — elle sera liée à votre compte.
            </div>
          )}
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="partner-landing-form">
            <Field label="Nom du partenaire (blog, site, influenceur…)" value={f.name} onChange={set("name")} testid="pl-name" autoFocus />
            <label className="block">
              <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">Type de partenaire</span>
              <select value={f.ptype} onChange={set("ptype")} data-testid="pl-type"
                className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
            <Field label="E-mail (servira à vous connecter)" type="email" value={f.email} onChange={set("email")} testid="pl-email" />
            <Field label="Mot de passe" type="password" value={f.password} onChange={set("password")} testid="pl-password" />
            <Field label="Confirmer le mot de passe" type="password" value={f.confirm} onChange={set("confirm")} testid="pl-confirm" />
            <button type="submit" disabled={loading} data-testid="partner-landing-submit"
              className="group mt-2 inline-flex w-full items-center justify-center gap-2 bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60">
              {loading ? "Création…" : "Créer mon compte partenaire"}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
          <p className="mt-6 text-sm text-ink-secondary">
            Déjà partenaire ? <Link to="/connexion" data-testid="pl-to-login" className="text-cyan hover:underline">Connectez-vous</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, title, text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-cyan/30 bg-cyan/10"><Icon size={15} className="text-cyan" /></span>
      <div>
        <div className="text-sm font-600 text-white">{title}</div>
        <div className="text-xs text-ink-secondary">{text}</div>
      </div>
    </li>
  );
}

function Field({ label, type = "text", value, onChange, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input required type={type} value={value} onChange={onChange} data-testid={testid} autoFocus={autoFocus}
        className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan" />
    </label>
  );
}
